"use client";

import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Loader as Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any)?.wvcClient;
interface FormFieldDefinition {
  id: string;
  key: string;
  type: string;
  label: string;
  placeholder?: string;
  help?: string;
  required?: boolean;
}
interface FormDefinition {
  key: string;
  name: string;
  fields: FormFieldDefinition[];
  submit?: {
    label?: string;
    processing_label?: string;
  };
  spam?: {
    token?: string;
    min_seconds?: number;
    honeypot?: {
      field: string;
    } | null;
  };
}
export default function ContactUs() {
  const [formDef, setFormDef] = useState<FormDefinition | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [hpValue, setHpValue] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    message: string;
  }>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState<string>("");
  const fetchedAtRef = useRef<number>(0);
  const fetchDefinition = async () => {
    setLoading(true);
    setLoadError(null);
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setLoadError("Service is currently unavailable. Please try again later.");
      setLoading(false);
      return;
    }
    try {
      const res = await client.pluginRequest("wvc-forms/v1/forms/contact_us");
      setFormDef(res);
      fetchedAtRef.current = Date.now();
    } catch (err: any) {
      if (err?.status === 404) {
        try {
          const listRes = await client.pluginRequest("wvc-forms/v1/forms");
          const items = listRes?.items || [];
          const matched = items.find((item: any) => item.key === "contact_us") || (items.length === 1 ? items[0] : null) || items.find((item: any) => item.name?.toLowerCase().includes("contact"));
          if (matched) {
            const fallbackDef = await client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
            setFormDef(fallbackDef);
            fetchedAtRef.current = Date.now();
          } else {
            setLoadError("Form configuration could not be found.");
          }
        } catch {
          setLoadError("Unable to load contact form.");
        }
      } else {
        setLoadError("Unable to load the form. Please refresh or try again.");
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchDefinition();
  }, []);
  const getFieldIdByKey = (keyName: string): string => {
    if (!formDef?.fields) return keyName;
    const found = formDef.fields.find(f => f.key === keyName);
    return found ? found.id : keyName;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDef || isSubmitting) return;
    setFieldErrors({});
    setFormError(null);

    // Enforce spam minimum seconds check if defined
    const minSeconds = formDef.spam?.min_seconds ?? 3;
    const elapsedSeconds = (Date.now() - fetchedAtRef.current) / 1000;
    if (elapsedSeconds < minSeconds) {
      const waitTime = Math.ceil((minSeconds - elapsedSeconds) * 1000);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    setIsSubmitting(true);
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setFormError("Unable to submit right now. Please try again.");
      setIsSubmitting(false);
      return;
    }

    // Map payload by field IDs
    const submitValues: Record<string, string> = {};
    const nameFieldId = getFieldIdByKey("name");
    const emailFieldId = getFieldIdByKey("email");
    const phoneFieldId = getFieldIdByKey("phone");
    const messageFieldId = getFieldIdByKey("message");
    if (nameFieldId) submitValues[nameFieldId] = values.name;
    if (emailFieldId) submitValues[emailFieldId] = values.email;
    if (phoneFieldId && values.phone) submitValues[phoneFieldId] = values.phone;
    if (messageFieldId) submitValues[messageFieldId] = values.message;
    const payload: Record<string, any> = {
      values: submitValues,
      token: formDef.spam?.token,
      meta: {
        source_url: typeof window !== "undefined" ? window.location.href : "",
        referrer: typeof document !== "undefined" ? document.referrer : ""
      }
    };
    if (formDef.spam?.honeypot?.field) {
      payload.hp = {
        [formDef.spam.honeypot.field]: hpValue
      };
    }
    try {
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}/submit`, {
        method: "POST",
        data: payload
      });
      if (res && res.success) {
        setIsSubmitted(true);
        if (res.confirmation?.message) {
          setConfirmationMessage(res.confirmation.message);
        } else {
          setConfirmationMessage("Thanks for reaching out — we'll get back to you shortly.");
        }
      } else if (res && res.code === "frms_validation_failed") {
        setFieldErrors(res.errors ?? {});
      } else if (res && res.code === "frms_invalid_token") {
        await fetchDefinition();
        setFormError("Your session expired. Please submit your message again.");
      } else {
        setFormError(res?.message || "There was a problem submitting your message. Please try again.");
      }
    } catch (err: any) {
      setFormError(err?.message || "Connection error. Please try again shortly.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const nameFieldId = getFieldIdByKey("name");
  const emailFieldId = getFieldIdByKey("email");
  const phoneFieldId = getFieldIdByKey("phone");
  const messageFieldId = getFieldIdByKey("message");
  return <section id="contact-us" data-nav="light" className="bg-background text-foreground py-20 lg:py-28">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif uppercase tracking-widest text-center mb-10 text-foreground font-semibold">
          Contact Us
        </h2>

        {loading ? <div className="space-y-6">
            <div className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-28 w-full" />
            </div>
            <Skeleton className="h-11 w-full" />
          </div> : loadError ? <Alert variant="destructive" className="border border-destructive/30">
            <AlertCircle className="size-4" />
            <AlertTitle>Notice</AlertTitle>
            <AlertDescription className="flex items-center justify-between gap-4 mt-2">
              <span>{loadError}</span>
              <Button variant="outline" size="sm" onClick={fetchDefinition} className="shrink-0">
                Retry
              </Button>
            </AlertDescription>
          </Alert> : <AnimatePresence mode="wait">
            {isSubmitted ? <motion.div key="confirmation" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -16
        }} transition={{
          duration: 0.4,
          ease: "easeOut"
        }}>
                <Alert className="border-secondary/40 bg-accent text-accent-foreground py-6 px-6">
                  <CheckCircle2 className="size-5 text-secondary" />
                  <AlertTitle className="text-base font-semibold">
                    Message Sent
                  </AlertTitle>
                  <AlertDescription className="mt-2 text-foreground/90 text-sm leading-relaxed">
                    {confirmationMessage ? <div dangerouslySetInnerHTML={{
                __html: confirmationMessage
              }} /> : "Thanks for reaching out — we'll get back to you shortly."}
                  </AlertDescription>
                </Alert>
              </motion.div> : <motion.form key="form" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -16
        }} transition={{
          duration: 0.4,
          ease: "easeOut"
        }} onSubmit={handleSubmit} className="space-y-6" noValidate>
                {/* Honeypot field (hidden for spam prevention) */}
                {formDef?.spam?.honeypot?.field && <div aria-hidden="true" className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden">
                    <label htmlFor={formDef.spam.honeypot.field}>Leave this empty</label>
                    <input id={formDef.spam.honeypot.field} name={formDef.spam.honeypot.field} type="text" tabIndex={-1} autoComplete="off" value={hpValue} onChange={e => setHpValue(e.target.value)} />
                  </div>}

                {/* Form General Error Alert */}
                {formError && <Alert variant="destructive">
                    <AlertCircle className="size-4" />
                    <AlertTitle>Submission failed</AlertTitle>
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>}

                {/* Name Field */}
                <div className="space-y-2">
                  <Label htmlFor="contact-name" className="text-foreground">
                    Name
                  </Label>
                  <Input id="contact-name" type="text" required value={values.name} onChange={e => setValues(prev => ({
              ...prev,
              name: e.target.value
            }))} placeholder="Your name" className="border-input focus-visible:border-secondary focus-visible:ring-secondary/30 transition-all rounded-xs" aria-invalid={Boolean(fieldErrors[nameFieldId])} />
                  {fieldErrors[nameFieldId] && <p className="text-xs text-destructive mt-1">
                      {fieldErrors[nameFieldId].message}
                    </p>}
                </div>

                {/* Email Field */}
                <div className="space-y-2">
                  <Label htmlFor="contact-email" className="text-foreground">
                    Email
                  </Label>
                  <Input id="contact-email" type="email" required value={values.email} onChange={e => setValues(prev => ({
              ...prev,
              email: e.target.value
            }))} placeholder="name@example.com" className="border-input focus-visible:border-secondary focus-visible:ring-secondary/30 transition-all rounded-xs" aria-invalid={Boolean(fieldErrors[emailFieldId])} />
                  {fieldErrors[emailFieldId] && <p className="text-xs text-destructive mt-1">
                      {fieldErrors[emailFieldId].message}
                    </p>}
                </div>

                {/* Phone Field (Optional) */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="contact-phone" className="text-foreground">
                      Phone
                    </Label>
                    <span className="text-xs text-muted-foreground">Optional</span>
                  </div>
                  <Input id="contact-phone" type="tel" value={values.phone} onChange={e => setValues(prev => ({
              ...prev,
              phone: e.target.value
            }))} placeholder="Contact number" className="border-input focus-visible:border-secondary focus-visible:ring-secondary/30 transition-all rounded-xs" aria-invalid={Boolean(fieldErrors[phoneFieldId])} />
                  {fieldErrors[phoneFieldId] && <p className="text-xs text-destructive mt-1">
                      {fieldErrors[phoneFieldId].message}
                    </p>}
                </div>

                {/* Message Field */}
                <div className="space-y-2">
                  <Label htmlFor="contact-message" className="text-foreground">
                    Message
                  </Label>
                  <Textarea id="contact-message" required rows={5} value={values.message} onChange={e => setValues(prev => ({
              ...prev,
              message: e.target.value
            }))} placeholder="How can we assist you?" className="border-input focus-visible:border-secondary focus-visible:ring-secondary/30 transition-all min-h-32 rounded-xs" aria-invalid={Boolean(fieldErrors[messageFieldId])} />
                  {fieldErrors[messageFieldId] && <p className="text-xs text-destructive mt-1">
                      {fieldErrors[messageFieldId].message}
                    </p>}
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <Button type="submit" size="lg" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-xs font-semibold tracking-wide uppercase transition-colors">
                    {isSubmitting ? <>
                        <Loader2 className="size-4 animate-spin" />
                        <span>Sending…</span>
                      </> : formDef?.submit?.label || "Send Message"}
                  </Button>
                </div>
              </motion.form>}
          </AnimatePresence>}
      </div>
    </section>;
}